import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import {
  LandingFeeException,
  LandingFeeExceptionUpdatePayload,
  useUpdateLandingFeeException,
} from "@/queries/landing";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  ExceptionFormValues,
  LandingExceptionForm,
  centsToDollarString,
  exceptionFormSchema,
  numToString,
} from "./LandingExceptionForm";

interface UpdateLandingExceptionDialogProps {
  exception: LandingFeeException;
}

export function UpdateLandingExceptionDialog({
  exception,
}: UpdateLandingExceptionDialogProps) {
  const { mutate: updateException, isPending: isUpdatingException } =
    useUpdateLandingFeeException();
  const { toast } = useToast();
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const form = useForm<ExceptionFormValues>({
    resolver: zodResolver(exceptionFormSchema),
    // Default values will be overridden by useEffect below
    defaultValues: {},
  });

  // Pre-fill form when the dialog opens or the exception data changes
  useEffect(() => {
    if (isUpdateModalOpen && exception) {
      let airline_code_iata = "";
      let min_weight_lbs: number | null = null;
      let max_weight_lbs: number | null = null;
      let tail_number = "";

      if (
        exception.criteria_type.toLowerCase() === "airlineexceptioncriteria"
      ) {
        airline_code_iata =
          (exception.criteria as any)?.airline_code_iata || "";
      } else if (
        exception.criteria_type.toLowerCase() === "weightexceptioncriteria"
      ) {
        min_weight_lbs = (exception.criteria as any)?.min_weight_lbs ?? null;
        max_weight_lbs = (exception.criteria as any)?.max_weight_lbs ?? null;
      } else if (
        exception.criteria_type.toLowerCase() === "tailnumberexceptioncriteria"
      ) {
        tail_number = (exception.criteria as any)?.tail_number || "";
      }

      form.reset({
        name: exception.name,
        description: exception.description ?? "",
        status: exception.status,
        signatory_status: exception.signatory_status ?? "BOTH",
        // Convert cents from API back to string dollar value for input
        fixed_fee_cents: centsToDollarString(exception.fixed_fee_cents),
        // Determine criteria type based on incoming data structure
        criteria_type:
          exception.criteria_type.toLowerCase() === "airlineexceptioncriteria"
            ? "airline"
            : exception.criteria_type.toLowerCase() ===
                "weightexceptioncriteria"
              ? "weight"
              : "tail_number",
        airline_code_iata: airline_code_iata,
        tail_number: tail_number,
        // Convert weight numbers from API back to strings for input
        min_weight_lbs: numToString(min_weight_lbs),
        max_weight_lbs: numToString(max_weight_lbs),
        // Convert date strings from API to Date objects for form
        effective_from: exception.effective_from
          ? new Date(exception.effective_from)
          : null,
        effective_until: exception.effective_until
          ? new Date(exception.effective_until)
          : null,
      });
    } else if (!isUpdateModalOpen) {
      form.reset({}); // Clear form when closing
    }
  }, [isUpdateModalOpen, exception]);

  // --- Submit Handler ---
  const onExceptionSubmit = (values: ExceptionFormValues) => {
    // --- Convert validated strings to numbers/null for API ---
    const final_fixed_fee_cents =
      values.fixed_fee_cents && values.fixed_fee_cents.trim() !== ""
        ? Math.round(parseFloat(values.fixed_fee_cents) * 100)
        : null;

    const final_min_weight_lbs =
      values.min_weight_lbs && values.min_weight_lbs.trim() !== ""
        ? parseInt(values.min_weight_lbs)
        : null;
    const final_max_weight_lbs =
      values.max_weight_lbs && values.max_weight_lbs.trim() !== ""
        ? parseInt(values.max_weight_lbs)
        : null;

    let criteria_data: Record<string, any> = {};
    if (values.criteria_type === "airline") {
      criteria_data = { airline_code_iata: values.airline_code_iata?.trim() };
    } else if (values.criteria_type === "weight") {
      if (final_min_weight_lbs !== null) {
        criteria_data.min_weight_lbs = final_min_weight_lbs;
      }
      if (final_max_weight_lbs !== null) {
        criteria_data.max_weight_lbs = final_max_weight_lbs;
      }
    } else if (values.criteria_type === "tail_number") {
      criteria_data = { tail_number: values.tail_number?.trim() };
    }
    // --- End Conversion ---

    const payload: LandingFeeExceptionUpdatePayload = {
      name: values.name,
      description: values.description || null,
      status: values.status,
      signatory_status: values.signatory_status,
      fixed_fee_cents: final_fixed_fee_cents,
      criteria_type: values.criteria_type,
      criteria_data: criteria_data as any,
      effective_from: values.effective_from
        ? format(values.effective_from, "yyyy-MM-dd")
        : null,
      effective_until: values.effective_until
        ? format(values.effective_until, "yyyy-MM-dd")
        : null,
    };

    updateException(
      { id: exception.id, payload },
      {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Landing fee exception updated.",
          });
          setIsUpdateModalOpen(false);
        },
        onError: (err) => {
          toast({
            variant: "destructive",
            title: "Error updating exception",
            description: err.message,
          });
        },
      }
    );
  };

  return (
    <Dialog open={isUpdateModalOpen} onOpenChange={setIsUpdateModalOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <Pencil className="h-4 w-4" />
          <span className="sr-only">Edit Exception</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Landing Fee Exception</DialogTitle>
          <DialogDescription>
            Modify the details for this exception rule.
          </DialogDescription>
        </DialogHeader>

        {/* Use the shared form component */}
        <div className="py-4">
          <LandingExceptionForm
            form={form}
            onSubmit={onExceptionSubmit}
            isLoading={isUpdatingException}
            submitButtonText="Save Changes"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
